/**
 * AdminEntityDetail - Generic entity detail component
 *
 * Features:
 * - Key-value form for all fields
 * - UUID foreign keys rendered as clickable links
 * - JSONB fields in collapsible JSON viewer
 * - Edit and delete actions
 * - Breadcrumb navigation
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { adminApi } from '../../../services/admin-api';

const isDateOnlyValue = (value: any): boolean =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T00:00:00/.test(value);

const toDateInputValue = (value: any): string =>
  typeof value === 'string' ? value.split('T')[0] : '';

export function AdminEntityDetail() {
  const { table, id } = useParams<{ table: string; id: string }>();
  const navigate = useNavigate();

  const [entity, setEntity] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userLeagues, setUserLeagues] = useState<any[]>([]);

  useEffect(() => {
    if (table && id) {
      loadEntity();
    }
  }, [table, id]);

  const loadEntity = async () => {
    if (!table || !id) return;

    try {
      setLoading(true);
      const data = await adminApi.getEntity(table, id);
      setEntity(data);
      setEditValues(data);

      // Load league memberships for users
      if (table === 'users' && data.id) {
        try {
          const memberships = await adminApi.listEntities('league_members', {
            filter_column: 'user_id',
            filter_value: data.id,
            limit: 100,
          });
          // Fetch league names for each unique league_id
          const leagueIds = [...new Set(memberships.rows.map((m: any) => m.league_id))];
          const leagueDetails = await Promise.all(
            leagueIds.map((lid: string) => adminApi.getEntity('leagues', lid).catch(() => null))
          );
          const leagueMap: Record<string, string> = {};
          leagueDetails.forEach((l: any) => {
            if (l) leagueMap[l.id] = l.name;
          });
          setUserLeagues(
            memberships.rows.map((m: any) => ({
              ...m,
              league_name: leagueMap[m.league_id] || 'Unknown',
            }))
          );
        } catch (err) {
          console.error('Failed to load user leagues:', err);
        }
      }
    } catch (error) {
      console.error('Failed to load entity:', error);
      alert('Failed to load entity');
      navigate(`/admin/entities/${table}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!table || !id) return;

    try {
      setSaving(true);
      // Only send changed fields
      const changes: Record<string, any> = {};
      Object.keys(editValues).forEach((key) => {
        if (JSON.stringify(editValues[key]) !== JSON.stringify(entity[key])) {
          changes[key] = editValues[key];
        }
      });

      if (Object.keys(changes).length === 0) {
        setEditing(false);
        return;
      }

      await adminApi.updateEntity(table, id, changes);
      await loadEntity();
      setEditing(false);
      alert('Entity updated successfully');
    } catch (error: any) {
      console.error('Failed to update entity:', error);
      alert(`Failed to update: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!table || !id) return;

    if (!confirm('Are you sure you want to delete this entity? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await adminApi.deleteEntity(table, id);
      alert('Entity deleted successfully');
      navigate(`/admin/entities/${table}`);
    } catch (error: any) {
      console.error('Failed to delete entity:', error);
      alert(`Failed to delete: ${error.message}`);
      setDeleting(false);
    }
  };

  const isUUID = (value: string): boolean => {
    return (
      typeof value === 'string' &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
    );
  };

  const isForeignKeyColumn = (key: string): boolean => {
    return key.endsWith('_id') && key !== 'id' && key !== 'cognito_id';
  };

  const getForeignKeyTable = (key: string): string => {
    // Remove _id suffix
    const base = key.replace(/_id$/, '');
    // Handle special cases
    if (base === 'tournament') return 'tournaments';
    if (base === 'user' || base === 'creator') return 'users';
    if (base === 'league') return 'leagues';
    if (base === 'wrestler') return 'wrestlers';
    if (base === 'league_member') return 'league_members';
    // Default: pluralize
    return base + 's';
  };

  const renderValue = (key: string, value: any): JSX.Element => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }

    // Handle boolean
    if (typeof value === 'boolean') {
      return (
        <span className={value ? 'text-green-600' : 'text-red-600'}>
          {value ? 'true' : 'false'}
        </span>
      );
    }

    // Handle dates
    if (key.endsWith('_at') || key.includes('date') || key.includes('time')) {
      try {
        // Date-only values (YYYY-MM-DDT00:00:00.000Z from DATE columns): show just the date
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T00:00:00/.test(value)) {
          const [year, month, day] = value.split('T')[0].split('-').map(Number);
          const localDate = new Date(year, month - 1, day);
          return (
            <span>
              {localDate.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          );
        }
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return <span>{date.toLocaleString()}</span>;
        }
      } catch {
        // Invalid date, fall through to default rendering
      }
    }

    // Handle foreign key UUIDs
    if (isForeignKeyColumn(key) && isUUID(value)) {
      const targetTable = getForeignKeyTable(key);
      return (
        <Link
          to={`/admin/entities/${targetTable}/${value}`}
          className="text-blue-600 hover:text-blue-800 underline"
          onClick={(e) => e.stopPropagation()}
        >
          {value.substring(0, 8)}...
        </Link>
      );
    }

    // Handle JSONB objects/arrays
    if (typeof value === 'object') {
      return (
        <details className="cursor-pointer">
          <summary className="text-blue-600 hover:text-blue-800">View JSON</summary>
          <pre className="mt-2 p-4 bg-gray-100 rounded text-xs overflow-x-auto">
            {JSON.stringify(value, null, 2)}
          </pre>
        </details>
      );
    }

    // Handle long strings
    if (typeof value === 'string' && value.length > 100) {
      return (
        <details className="cursor-pointer">
          <summary className="text-blue-600 hover:text-blue-800">
            {value.substring(0, 100)}...
          </summary>
          <div className="mt-2 p-4 bg-gray-100 rounded text-sm whitespace-pre-wrap break-words">
            {value}
          </div>
        </details>
      );
    }

    return <span>{value.toString()}</span>;
  };

  if (!table || !id) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">Invalid parameters</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">Entity not found</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Breadcrumb */}
      <div className="mb-6 flex items-center text-sm text-gray-500">
        <Link to="/admin" className="hover:text-gray-700">
          Admin
        </Link>
        <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <Link to={`/admin/entities/${table}`} className="hover:text-gray-700 capitalize">
          {table.replace(/_/g, ' ')}
        </Link>
        <svg className="w-4 h-4 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-gray-900">{id.substring(0, 8)}...</span>
      </div>

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 capitalize">
            {table.replace(/_/g, ' ')} Detail
          </h1>
          <p className="text-gray-500 mt-2 font-mono text-sm">{id}</p>
        </div>

        <div className="flex gap-2">
          {editing ? (
            <>
              <button
                onClick={() => {
                  setEditing(false);
                  setEditValues(entity);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Edit
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Entity Data */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="p-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6">
            {Object.keys(entity).map((key) => (
              <div key={key} className="border-b border-gray-100 pb-4">
                <dt className="text-sm font-medium text-gray-500 mb-2">{key}</dt>
                <dd className="text-sm text-gray-900">
                  {editing && !key.endsWith('_at') && key !== 'id' && key !== 'cognito_id' ? (
                    isDateOnlyValue(editValues[key]) ? (
                      <input
                        type="date"
                        value={toDateInputValue(editValues[key])}
                        onChange={(e) =>
                          setEditValues({
                            ...editValues,
                            [key]: e.target.value ? e.target.value + 'T00:00:00.000Z' : '',
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <input
                        type="text"
                        value={editValues[key]?.toString() || ''}
                        onChange={(e) => setEditValues({ ...editValues, [key]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )
                  ) : (
                    renderValue(key, entity[key])
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* User Leagues */}
      {table === 'users' && userLeagues.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Leagues ({userLeagues.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    League
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Points
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {userLeagues.map((m) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/admin/entities/leagues/${m.league_id}`}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {m.league_name}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {m.team_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {m.total_points ?? 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          m.status === 'approved'
                            ? 'bg-green-100 text-green-800'
                            : m.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {m.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {m.joined_at ? new Date(m.joined_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
